import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { type FrequencyModelDto } from '@treasury/api/channel';
import { Wire, WireIsoService, WiresService } from '@treasury/domain/wires';
// eslint-disable-next-line no-duplicate-imports
import type { WireIsoLabels } from '@treasury/domain/wires';
import { LabeledList, NotificationService, TmBaseComponent } from '@treasury/presentation';
import { mapFrequencyModelDtoToFrequency } from '@treasury/presentation/components/tm-frequency.mappings';
import { summarizeFrequency } from '@treasury/presentation/components/tm-frequency.summarizer';
import '@treasury/presentation/components/tm-status';
import '@treasury/presentation/components/tm-toggle-content';
import { camelCaseToTitle } from '@treasury/utils';
import { format } from 'date-fns';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export const tagName = 'wire-data-readonly';
@customElement(tagName)
export class WireDataReadonly extends TmBaseComponent {
    @property({ type: Object })
    public wire = new Wire();

    @property({ type: String })
    public wireTemplateName = '';

    @property({ type: Boolean })
    public creditorDetail = true;

    @property({ type: Boolean })
    public paymentDetail = false;

    @InjectProperty()
    declare private wireService: WiresService;

    @InjectProperty()
    declare private wireIsoService: WireIsoService;

    @InjectProperty()
    declare private notificationService: NotificationService;

    @property({ type: Object })
    private wireIsoLabels: WireIsoLabels = WireIsoService.legacyLabels;

    @state()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private wireDetail: any = {};

    @state()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private wireAdditional: any = {};

    @state()
    private hasWireIsoFeatureFlag = false;

    protected render() {
        if (!this.wire) {
            return nothing;
        }
        return html`<div class="px-6 py-4">
            <div class="border-b border-dashed">
                <div class="flex justify-between items-center">
                    <div class="text-lg font-medium mb-1">
                        ${this.wireTemplateName || this.wire.wireTemplate?.name || 'Wire Payment'}
                    </div>
                    <tm-badge uppercase>
                        ${this.wire.isInternational ? 'International' : 'Domestic'}
                    </tm-badge>
                </div>
                <div class="flex justify-between items-center">
                    <div class="text-2xl font-normal">
                        ${new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        }).format(this.wire.amount)}
                    </div>
                    ${this.wire.status
                        ? html`<tm-status .status=${this.wire.status}></tm-status>`
                        : nothing}
                </div>
                <tm-labeled-list class="py-4" .list=${this.renderWireDetail()}></tm-labeled-list>
            </div>
            <div class="border-b border-dashed overflow-x-auto">
                <tm-labeled-list
                    class="py-4"
                    .list=${this.renderOptionalInformation()}
                ></tm-labeled-list>
            </div>
            ${this.paymentDetail ? [this.renderCreatedDate(), this.renderAudit()] : nothing}
        </div> `;
    }

    protected async firstUpdated() {
        try {
            [this.hasWireIsoFeatureFlag, this.wireIsoLabels] = await Promise.all([
                this.wireIsoService.getEntitlementWireISO20022(),
                this.wireIsoService.getLabels(),
            ]);
        } catch (error) {
            if (error instanceof Error) {
                this.notificationService.renderError(error.message);
            }
        }
    }

    private renderWireDetail() {
        this.wireDetail = {
            wireCompany: this.wire.wireCompany?.name,
            debitAccount: this.wire.debitAccount?.accountDisplayLabel,
            creditor: this.wire.beneficiary.name,
        };
        if (this.wire.frequency?.type === 'OneTime') {
            this.wireDetail.frequency = camelCaseToTitle(this.wire.frequency.type as string);
            this.wireDetail.effectiveDate = format(
                this.wire.frequency.valueDate as string,
                'MM/dd/yyyy'
            );
        } else {
            this.wireDetail.frequency = summarizeFrequency(
                mapFrequencyModelDtoToFrequency(this.wire.frequency as FrequencyModelDto)
            );
        }
        if (this.wire.transactionId) {
            this.wireDetail.transactionId = this.wire.transactionId;
        }
        if (this.wire.wireFileId) {
            this.wireDetail.fileName = this.wire.fileName;
        }
        return new LabeledList(
            this.wireDetail,
            Object.keys(this.wireDetail),
            { creditor: this.wireIsoLabels.creditor },
            {
                creditor: this.creditorDetail ? 'view-creditor-detail' : undefined,
                fileName: 'wireFile',
            }
        );
    }

    private renderOptionalInformation() {
        this.wireAdditional.purpose = this.wire.purpose;

        this.wireAdditional.additionalInformation = this.wire.additionalInformationValues;

        if (!this.wire.isInternational || this.hasWireIsoFeatureFlag) {
            this.wireAdditional.referenceBeneficiary = this.wire.referenceBeneficiary;
        }

        return new LabeledList(this.wireAdditional, Object.keys(this.wireAdditional), {
            referenceBeneficiary: this.wireIsoLabels.endToEndId,
        });
    }

    private renderCreatedDate() {
        if (!this.wire.createdDate) {
            return nothing;
        }
        return html`<tm-labeled-list
            class="py-4"
            .list=${new LabeledList(
                {
                    creationDate: format(
                        this.wire.createdDate as unknown as string,
                        'MMMM do, yyyy'
                    ),
                },
                ['creationDate']
            )}
        ></tm-labeled-list>`;
    }

    private renderAudit() {
        if (!this.wire.audit) {
            return nothing;
        }
        return html`<tm-toggle-content label="audit" buttonType="secondary">
            <div class="pb-2">
                <div class="pb-2 font-medium">Audit</div>
                <div class="font-normal text-sm">${this.wire.audit}</div>
            </div>
        </tm-toggle-content>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: block;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WireDataReadonly;
    }
}
